import { AlertTriangle } from "lucide-react";
import { useEffect, useState } from "react";

import { useSidebar } from "@/components/sidebar";
import { useUser } from "@/hooks/useUser";
import { cn } from "@/utils/cn";
import { getEstDaysRemaining } from "@/utils/getEstDaysRemaining";

function getMsUntilNextEstMidnight(): number {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    hour: "2-digit",
    hour12: false,
    minute: "2-digit",
    second: "2-digit",
    timeZone: "America/New_York",
  }).formatToParts(now);
  const h = Number(parts.find((p) => p.type === "hour")!.value);
  const min = Number(parts.find((p) => p.type === "minute")!.value);
  const s = Number(parts.find((p) => p.type === "second")!.value);

  return (86_400 - (h * 3600 + min * 60 + s)) * 1000;
}

function useTrialCountdown(cancelledAt: Date | null | string): number {
  const [days, setDays] = useState(() =>
    cancelledAt ? getEstDaysRemaining(cancelledAt) : 0,
  );

  useEffect(() => {
    if (!cancelledAt || days <= 0) return;
    const id = setTimeout(() => {
      setDays(getEstDaysRemaining(cancelledAt));
    }, getMsUntilNextEstMidnight());

    return () => clearTimeout(id);
  }, [cancelledAt, days]);

  useEffect(() => {
    if (!cancelledAt) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        setDays(getEstDaysRemaining(cancelledAt));
      }
    };

    document.addEventListener("visibilitychange", onVisible);

    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [cancelledAt]);

  return days;
}

export function TrialRibbon() {
  const { currentProject, data } = useUser();
  const { isMobile, state } = useSidebar();

  const cancelled = currentProject?.subscriptionCancelledAt ?? null;
  const daysRemaining = useTrialCountdown(cancelled);

  if (!currentProject?.isFirstDraft || daysRemaining <= 0) return null;

  const firstName = data?.user.name.split(" ")[0] ?? "";

  return (
    <div
      className={cn(
        "flex shrink-0 items-center justify-between bg-utility-warning-400 px-6 py-3",
        "transition-[margin] duration-300 ease-in-out",
        !isMobile && (state === "collapsed" ? "ml-0" : "ml-[170px]"),
      )}
    >
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-0.5 size-5 shrink-0 text-text-primary" />
        <div className="flex flex-col gap-0.5">
          <span className="text-sm font-semibold text-text-primary">
            Hi {firstName}, your First Draft trial is underway.
          </span>
          <span className="text-sm text-text-primary">
            You have{" "}
            <span className="font-bold">{daysRemaining} days remaining</span> to
            use your design credit and explore the No Limit Creatives platform.
          </span>
        </div>
      </div>
      <a
        className="ml-6 shrink-0 text-sm font-semibold leading-5 underline text-text-primary"
        href="https://nlc.com/manage-my-account"
        rel="noreferrer"
        target="_blank"
      >
        Explore Design Plans
      </a>
    </div>
  );
}
